import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {map, Observable, tap} from 'rxjs';
import {BackofficePermissionsService} from './backoffice-permissions.service';
import {RolesService} from './roles-service';
import {BackofficeService} from '../_services/configuration-services';

export const PermissionGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> => {

    const backofficePermissionsService = inject(BackofficePermissionsService);
    const rolesService = inject(RolesService);
    const router = inject(Router);
    const backofficeService = inject(BackofficeService);

    const allowedPermissions = route.data['allowedPermissions'] || null;
    if (allowedPermissions === null) {
        return true;
    }

    // SuperAdmin bypasses all permission checks
    if (rolesService.hasRoleSuperAdmin()) {
        return true;
    }

    const evaluate = (): boolean | UrlTree => {
        if (backofficePermissionsService.hasAnyPermission(allowedPermissions)) {
            return true;
        }

        // Redirect to an alternative route if the user has the required permissions for it
        const alternativeRoute = route.data['alternativeRoute'] as string | null;
        const alternativePermissions = route.data['alternativePermissions'] as string[] | null;
        if (alternativeRoute && backofficePermissionsService.hasAnyPermission(alternativePermissions ?? [])) {
            return router.createUrlTree([alternativeRoute]);
        }

        return router.createUrlTree(['/services']);
    };

    // If permissions are already loaded, evaluate synchronously (normal navigation)
    if (backofficePermissionsService.permissionsLoaded$.value) {
        return evaluate();
    }

    // Permissions not yet loaded: this happens on initial page load (page refresh) where
    // AppComponent.ngOnInit() hasn't run yet due to initialNavigation: 'enabledBlocking'.
    // Customers and non-backoffice accounts will never have permissions — deny immediately.
    if (!rolesService.isBackofficeAccount()) {
        return router.createUrlTree(['/services']);
    }

    // For backoffice accounts: call getMe() directly to load permissions, then evaluate.
    // AppComponent will also call getMe() later for display name/side effects — this is acceptable.
    return backofficeService.getMe().pipe(
        tap(user => {
            backofficePermissionsService.setCurrentBackofficeId(user.id);
            backofficePermissionsService.setPermissions(
                (user.permissions ?? []).map(p => p.key)
            );
        }),
        map(evaluate)
    );
};
